import JSZip from "jszip";
import { toScratchblocks } from "utils/scratch-parser/index.js";

// HAT_BLOCKS to filter Scratch hat blocks
const HAT_BLOCKS = [
  "event_whenflagclicked",
  "event_whenkeypressed",
  "event_whengreaterthan",
  "event_whenthisspriteclicked",
  "event_whenstageclicked",
  "event_whenbackdropswitchesto",
  "event_whenbroadcastreceived",
  "control_start_as_clone",
  "procedures_definition",
  "boost_whenColor",
  "boost_whenTilted",
  "ev3_whenButtonPressed",
  "ev3_whenDistanceLessThan",
  "ev3_whenBrightnessLessThan",
  "gdxfor_whenGesture",
  "gdxfor_whenForcePushedOrPulled",
  "gdxfor_whenTilted",
  "makeymakey_whenMakeyKeyPressed",
  "makeymakey_whenCodePressed",
  "microbit_whenButtonPressed",
  "microbit_whenGesture",
  "microbit_whenTilted",
  "microbit_whenPinConnected",
  "wedo2_whenDistance",
  "wedo2_whenTilted",
];

const PREVENT_BLOCKS_HEAD = [
  "motion_xposition",
  "motion_yposition",
  "motion_direction",
  "looks_costumenumbername",
  "looks_backdropnumbername",
  "looks_size",
  "sound_volume",
  "sensing_touchingobject",
  "sensing_touchingcolor",
  "sensing_coloristouchingcolor",
  "sensing_distanceto",
  "sensing_answer",
  "sensing_keypressed",
  "sensing_mousedown",
  "sensing_mousex",
  "sensing_mousey",
  "sensing_loudness",
  "sensing_timer",
  "sensing_of",
  "sensing_current",
  "sensing_dayssince2000",
  "sensing_username",
  "operator_add",
  "operator_subtract",
  "operator_multiply",
  "operator_divide",
  "operator_random",
  "operator_gt",
  "operator_lt",
  "operator_equals",
  "operator_and",
  "operator_or",
  "operator_not",
  "operator_join",
  "operator_letter_of",
  "operator_length",
  "operator_contains",
  "operator_mod",
  "operator_round",
  "operator_mathop",
  "data_itemoflist",
  "data_itemnumoflist",
  "data_lengthoflist",
  "data_listcontainsitem",
  "procedures_call",
  "argument_reporter_string_number",
  "argument_reporter_boolean",
];

/**
 * Function used to transform a SB3 file in JSON format to text
 * @param jsonSb3File
 * @returns
 */
export const jsonSB3ToText = async (jsonSb3File: any) => {
  // Get the sprites of this json file
  const spriteOptions = jsonSb3File.targets
    .filter((target: any) => !target.isStage)
    .map((target: any) => target.name);

  // Define the outputs
  let outText = "";
  const outObj: any = {};

  // For each one of the sprites
  spriteOptions.forEach((targetName: string) => {
    // Get the scratch blocks for this sprite
    const thisBlocks = generateScratchblocks(
      jsonSb3File.targets.filter((item: any) => item.name === targetName)[0]
    );

    // Format the text output
    if (outText.length > 0) outText += "\n\n";
    outText += `# ${targetName}\n`;
    outText += thisBlocks;

    // And format the JSON output
    outObj[targetName] = thisBlocks;
  });

  // Define the output
  return { text: outText, obj: outObj };
};

/**
 * Function used to transform a raw sb3 file into a text or json file.
 * @param sb3File SB3 file in base64 format (string).
 * @param to (optional) Output format. By default is `"text"`.
 * @returns
 */
export const convertSB3File = async (
  sb3File: Buffer | string | null | undefined,
  to: "string" | "json" = "string"
) => {
  // Check if there's any content (return empty string or undefined)
  if (!sb3File) return sb3File;

  try {
    // Define the sb3Buffer
    let sb3Buffer: Buffer | undefined;

    // Base64 String case
    if (typeof sb3File === "string") {
      // Extract the base64 part from the Data URL
      const base64Match = sb3File.match(/^data:[^;]+;base64,(.+)$/);
      if (!base64Match) {
        throw new Error("Invalid Data URL format");
      }

      // Get the data
      const base64Data = base64Match[1];

      // Decode base64 to binary
      sb3Buffer = Buffer.from(base64Data, "base64");
    }
    // Buffer case
    else if (typeof sb3File === "object") {
      sb3Buffer = sb3File;
    } else {
      return null;
    }

    // Check the buffer is defined appropriately
    if (!sb3Buffer) return null;

    // Check if the buffer starts with the ZIP file signature
    if (sb3Buffer.readUInt32BE(0) !== 0x504b0304) {
      throw new Error("Invalid ZIP file signature");
    }

    // Use JSZip to read the project.json file
    const zip = await JSZip.loadAsync(sb3Buffer as any);

    // Get the project file
    const projectJsonFile = zip.file("project.json");
    if (!projectJsonFile) {
      throw new Error("project.json not found in the sb3 file");
    }
    
    // Get the content
    const projectJsonContent = await projectJsonFile.async("string");
    const projectJson = JSON.parse(projectJsonContent);

    // Transform this JSON file to text
    const { text, obj } = await jsonSB3ToText(projectJson);

    // Define the output
    const output = to === "string" ? text : obj;

    return output ? output : null;
  } catch (error) {
    console.error("Error processing sb3 file:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Stack trace:", error.stack);
    }
    return null;
  }
};

const generateScratchblocks = (target: any) => {
  const hatBlocks = Object.keys(target.blocks).filter((key) => {
    const blockItem = target.blocks[key];
    return (
      blockItem.topLevel && !PREVENT_BLOCKS_HEAD.includes(blockItem.opcode)
    );
  });

  const scratchblocksCode = hatBlocks
    .map((hatKey) => {
      return toScratchblocks(hatKey, target.blocks, "en", {
        tab: " ".repeat(4),
        variableStyle: "as-needed",
      });
    })
    .join("\n\n");

  return scratchblocksCode;
};
